import React, { useCallback, useEffect, useState } from 'react';
import { PERSON_TYPES } from '@/constants/constants.js';
import {
  firstOrThirdPersonOptions,
  genderOptions,
  useInCeremonyOptions,
} from '@/constants/dropdownOptions.js';
import { validate } from '@/utils/validationSchemas.js';
import TextInput from '@/components/TextInput.js';
import SelectInput from '@/components/SelectInput.js';
import CheckboxGroup from '@/components/CheckboxGroup.js';
import ImageUpload from '../upload/ImageUpload.js';
import AudioUpload from '../upload/AudioUpload.js';
import { useFormik } from 'formik';
import { getVideoTypes } from '@/features/systemManagement/videoType/videoTypeService.js';
import {
  autoSaveValue,
  updateVideoTypeAction,
} from '@/features/orders/ordering/services/orderService.js';
import {
  updateFileFieldValue,
} from '@/features/orders/ordering/services/orderMediaService.js';
import {
  updateTaskStatus,
} from '@/features/orders/ordering/services/orderWorkflowService.js';
import {
  ALL_TABLE_TYPES,
  ORDER_STATUS_TYPES,
  TABLE_FIELD_TYPES,
  VIDEO_TYPES,
} from '@/constants/constants.js';
import './Forms.scss';

export const useClientInfoForm = (
  client_id,
  user_id,
  totalImagesLimit,
  dynamicImageLimit,
  audioPartVisible,
  navigate,
  setIsSubmitting,
  originalClientInfo
) => {
  return useFormik({
    initialValues: {
      client_id: client_id || '',
      user_id: user_id || '',
      clientName: '',
      ceremonyTheme: '',
      videoType: '',
      custom_price: '',
      ceremonyDate: '',
      texts: '',
      relationship: '',
      firstOrThirdPerson: firstOrThirdPersonOptions[0].value,
      images: [],
      imageDescriptions: [],
      imageSelections: [],
      deceased_audio: [],
      deceased_audioDescriptions: [],
      narrator_audio: [],
      narrator_audioDescriptions: [],
      notes: '',
      status: 9,
      salesPerson: '',
      telephone: '',
      useInCeremony: null,
      gender: '',
    },
    validate: (values) => validate(values, audioPartVisible),
    validateOnChange: true,
    validateOnBlur: true,
    onSubmit: async (values, { setSubmitting, setStatus, setErrors }) => {
      try {
        // 照片数量验证
        const selectedCount = values.images.filter(
          (state) => state.image_dynamic_AI === true
        ).length;
        if (values.images.length > totalImagesLimit) {
          alert(`照片数量不能超过 ${totalImagesLimit} 张`);
          return;
        } else if (selectedCount > dynamicImageLimit) {
          alert(`动态化照片数量不能超过 ${dynamicImageLimit} 张`);
          return;
        }

        // 序号为空验证
        const hasEmptySortOrder = values.images.some(
          (image) => image.sort_order === null || image.sort_order === undefined || image.sort_order === ''
        );
        if (hasEmptySortOrder) {
          alert('请为所有照片填写序号');
          return;
        }

        setIsSubmitting(true);

        let status = '';

        if (values.status === ORDER_STATUS_TYPES.未提交) {
          if (
            values.videoType === VIDEO_TYPES.数字音容版 ||
            values.videoType === VIDEO_TYPES.数字音容Plus || 
            values.videoType === VIDEO_TYPES.数字音容版25年版本
          ) {
            status = ORDER_STATUS_TYPES.审核中;
          } else {
            status = ORDER_STATUS_TYPES.文案确认中;
          }
        } else if (values.status === ORDER_STATUS_TYPES.资料修改中) {
          if (
            values.videoType !== VIDEO_TYPES.数字音容版 &&
            values.videoType !== VIDEO_TYPES.数字音容Plus &&
            values.videoType !== VIDEO_TYPES.数字音容版25年版本 &&
            originalClientInfo.farewell_text === '' &&
            originalClientInfo.speech_text === ''
          ) {
            status = ORDER_STATUS_TYPES.文案确认中;
          } else {
            status = ORDER_STATUS_TYPES.审核中;
          }
        } else {
          status = values.status;
        }

        const response = await updateTaskStatus(client_id, status);
        setIsSubmitting(false);

        setStatus({ success: true });

        if (status === ORDER_STATUS_TYPES.审核中) {
          navigate(`/review/${client_id}`);
          return response;
        } else {
          navigate(`/${status}/${client_id}`);
          return response;
        }
      } catch (error) {
        setIsSubmitting(false);
        console.error('提交客户信息失败:', error);

        setStatus({
          success: false,
          error: error.message || '提交客户信息失败',
        });

        // 设置通用表单错误
        setErrors({ submit: error.message || '提交客户信息失败' });

        throw error;
      } finally {
        // 确保提交状态重置
        setSubmitting(false);
      }
    },
  });
};

const ClientInfoForm = ({
  formik,
  client_id,
  setVideoTypeLimits,
  totalImagesLimit,
  dynamicImageLimit,
  audioPartVisible,
}) => {
  const [videoTypeOptions, setVideoTypeOptions] = useState([]);
  const [videoTypesRaw, setVideoTypesRaw] = useState([]);

  const selectedVideoObj = videoTypesRaw.find((v) => v.title === formik.values.videoType);
  const localHasCustomPrice = selectedVideoObj ? !!selectedVideoObj.has_custom_price : false;

  // 加载视频类型数据
  useEffect(() => {
    const loadVideoTypes = async () => {
      try {
        const types = await getVideoTypes();
        setVideoTypesRaw(types);
        const options = types.map((type) => ({
          value: type.title,
          label: type.title,
        }));
        setVideoTypeOptions(options);
      } catch (error) {
        console.error('Failed to load video types:', error);
        setVideoTypeOptions([]);
      }
    };

    loadVideoTypes();
  }, []);

  useEffect(() => {
    setVideoTypeLimits(formik.values.videoType);
  }, [formik, setVideoTypeLimits]);

  const customHandleChange = useCallback(
    async (e) => {
      const { name, value } = e.target;
      formik.handleChange(e);
      try {
        if (value !== null && value !== undefined && value !== '') {
          await autoSaveValue(client_id, name, value);
        }
      } catch (error) {
        console.error('自动保存失败:', error);
      }
    },
    [formik, client_id]
  );

  // 视频类型变更逻辑
  const handleVideoTypeChange = useCallback(
    async (e) => {
      const selected = e.target.value;
      
      // Update formik state
      formik.handleChange(e);
      formik.setFieldValue('videoType', selected);
      setVideoTypeLimits(selected);

      // Call the specific API for updating video type instead of generic autoSaveValue
      const matchedType = videoTypesRaw.find(t => t.title === selected);
      if (matchedType) {
        try {
          await updateVideoTypeAction(client_id, matchedType.id, formik.values.custom_price || '');
        } catch (error) {
          console.error('更新视频套餐失败', error);
        }
      }
    },
    [formik, setVideoTypeLimits, client_id, videoTypesRaw]
  );

  // 文件管理逻辑
  const handleFileUpload = (newFiles, fileType) => {
    formik.setFieldValue(fileType, [...newFiles]);
  };

  const handleFileDelete = (newFiles, fileType) => {
    formik.setFieldTouched(fileType, true, false);
    formik.setFieldValue(fileType, newFiles, true);
  };

  const handleFileReplace = (newFile, fileType, index) => {
    const newFiles = [...formik.values[fileType]];
    newFiles[index] = newFile;
    formik.setFieldValue(fileType, newFiles);
  };

  const handleUpdateImages = (newFiles, fileType) => {
    formik.setFieldValue(fileType, [...newFiles]);
  };

  const handleAudioUpdateDescription = async (type, newAudio) => {
    await updateFileFieldValue(
      ALL_TABLE_TYPES.audio,
      TABLE_FIELD_TYPES.audio_text,
      newAudio[0].id,
      newAudio[0].description
    );
    formik.setFieldValue(type, newAudio);
  };

  return (
    <form className="row g-3" style={{ width: '100%' , borderBottom:'none'}}>
      <TextInput
        name="clientName"
        label="逝者名字"
        type="text"
        value={formik.values.clientName}
        onChange={customHandleChange}
        placeholder="请输入逝者名字"
        error={formik.errors.clientName}
        touched={formik.touched.clientName}
        onBlur={formik.handleBlur}
        className="col-md-8"
      />
      <SelectInput
        name="videoType"
        label="视频标准"
        options={videoTypeOptions}
        value={formik.values.videoType}
        onChange={handleVideoTypeChange}
        error={formik.errors.videoType}
        touched={formik.touched.videoType}
        onBlur={formik.handleBlur}
        className="col-md-8"
        required={true}
      />
      {localHasCustomPrice && (
        <TextInput
          name="custom_price"
          label="定制价格"
          type="number"
          value={formik.values.custom_price}
          onChange={customHandleChange}
          placeholder="请输入定制价格"
          error={formik.errors.custom_price}
          touched={formik.touched.custom_price}
          onBlur={formik.handleBlur}
          className="col-md-4"
        />
      )}
      <TextInput
        name="ceremonyDate"
        label="仪式时间"
        type="date"
        value={formik.values.ceremonyDate}
        onChange={customHandleChange}
        placeholder="请选择日期"
        error={formik.errors.ceremonyDate}
        touched={formik.touched.ceremonyDate}
        onBlur={formik.handleBlur}
        className="col-md-4"
      />
      <CheckboxGroup
        name="useInCeremony"
        className={'col-md-4'}
        label="是否在仪式中使用视频"
        options={useInCeremonyOptions}
        value={formik.values.useInCeremony}
        onChange={(value) => {
          const mockEvent = {
            target: {
              name: 'useInCeremony',
              value: value,
            },
          };
          customHandleChange(mockEvent);
        }}
        error={formik.errors.useInCeremony}
        touched={formik.touched.useInCeremony}
        onBlur={formik.handleBlur}
      />
      <CheckboxGroup
        name="gender"
        className={'col-md-4'}
        label="逝者性别"
        options={genderOptions}
        value={formik.values.gender}
        onChange={(value) => {
          const mockEvent = {
            target: {
              name: 'gender',
              value: value,
            },
          };
          customHandleChange(mockEvent);
        }}
        error={formik.errors.gender}
        touched={formik.touched.gender}
        onBlur={formik.handleBlur}
      />
      <TextInput
        name="texts"
        label="逝者生平"
        type="textarea"
        value={formik.values.texts}
        onChange={customHandleChange}
        placeholder="请输入逝者生平"
        style={{ minHeight: '10rem' }}
        error={formik.errors.texts}
        touched={formik.touched.texts}
        onBlur={formik.handleBlur}
        className="col-md-8"
      />
      <CheckboxGroup
        name="firstOrThirdPerson"
        className={'col-md-4'}
        label="讲述逝者生平"
        options={firstOrThirdPersonOptions}
        value={formik.values.firstOrThirdPerson}
        onChange={(value) => {
          const mockEvent = {
            target: {
              name: 'firstOrThirdPerson',
              value: value,
            },
          };
          customHandleChange(mockEvent);
        }}
        error={formik.errors.firstOrThirdPerson}
        touched={formik.touched.firstOrThirdPerson}
        onBlur={formik.handleBlur}
      />
      {formik.values.firstOrThirdPerson === PERSON_TYPES.THIRD_PERSON && (
        <>
          <TextInput
            name="relationship"
            label="讲述者为逝者的"
            type="text"
            value={formik.values.relationship}
            onChange={customHandleChange}
            placeholder="例：女儿；挚友"
            error={formik.errors.relationship}
            touched={formik.touched.relationship}
            onBlur={formik.handleBlur}
            required={formik.values.firstOrThirdPerson === PERSON_TYPES.THIRD_PERSON}
            className="col-md-8"
          />
          {audioPartVisible && (
            <>
              <AudioUpload
                label="讲述者声音"
                audio_type="narrator_audio"
                client_id={formik.values.client_id}
                onUpload={(newFiles) => {
                  handleFileUpload(
                    newFiles,
                    'narrator_audio',
                    formik.values.client_id
                  );
                }}
                onDelete={(newFiles) =>
                  handleFileDelete(newFiles, 'narrator_audio')
                }
                onReplace={(newFile, index) => {
                  handleFileReplace(newFile, 'narrator_audio', index);
                }}
                onUpdateDescription={(newAudio) =>
                  handleAudioUpdateDescription('narrator_audio', newAudio)
                }
                files={formik.values.narrator_audio}
                descriptions={formik.values.narrator_audioDescriptions}
                selections={formik.values.narrator_audioSelections}
                descriptionError={formik.errors.narrator_audioDescriptions}
                required={false}
              />
              {formik.errors.narrator_audio &&
                formik.touched.narrator_audio && (
                  <div className="error">{formik.errors.narrator_audio}</div>
                )}
            </>
          )}
        </>
      )}

      {audioPartVisible && (
        <>
          <AudioUpload
            label="逝者声音"
            audio_type="deceased_audio"
            client_id={formik.values.client_id}
            onUpload={(newFiles) => {
              handleFileUpload(
                newFiles,
                'deceased_audio',
                formik.values.client_id
              );
            }}
            onDelete={(newFiles) =>
              handleFileDelete(newFiles, 'deceased_audio')
            }
            onReplace={(newFile, index) => {
              handleFileReplace(newFile, 'deceased_audio', index);
            }}
            onUpdateDescription={(newAudio) =>
              handleAudioUpdateDescription('deceased_audio', newAudio)
            }
            files={formik.values.deceased_audio}
            descriptions={formik.values.deceased_audioDescriptions}
            selections={formik.values.deceased_audioSelections}
            descriptionError={formik.errors.deceased_audioDescriptions}
            required={false}
          />
          {formik.errors.deceased_audio && formik.touched.deceased_audio && (
            <div className="error">{formik.errors.deceased_audio}</div>
          )}
        </>
      )}

      <ImageUpload
        label={'逝者照片'}
        client_id={formik.values.client_id}
        onUpload={(newFiles) => {
          handleFileUpload(newFiles, 'images', formik.values.client_id);
        }}
        onDelete={(newFiles) => handleFileDelete(newFiles, 'images')}
        onReplace={(newFile, index) => {
          handleFileReplace(newFile, 'images', index);
        }}
        onUpdateImages={(newFiles) => handleUpdateImages(newFiles, 'images')}
        files={formik.values.images}
        totalImagesLimit={totalImagesLimit}
        dynamicImageLimit={dynamicImageLimit}
      />
      {formik.errors.images && formik.touched.images && (
        <div className="error">{formik.errors.images}</div>
      )}
      <TextInput
        name="salesPerson"
        label="业务员"
        type="text"
        value={formik.values.salesPerson}
        onChange={customHandleChange}
        placeholder="选填字段，请输入业务员姓名"
        className="col-md-5"
        required={false}
      />
      <TextInput
        name="telephone"
        label="主事人手机号"
        type="text"
        value={formik.values.telephone}
        onChange={customHandleChange}
        placeholder="请输入主事人手机号"
        error={formik.errors.telephone}
        touched={formik.touched.telephone}
        onBlur={formik.handleBlur}
        className="col-md-5"
      />
      <TextInput
        name="notes"
        label="视频备注"
        type="textarea"
        value={formik.values.notes}
        onChange={customHandleChange}
        placeholder="请输入视频备注"
        style={{ minHeight: '10rem' }}
        required={false}
        className="col-md-8"
      />
    </form>
  );
};

export default ClientInfoForm;
